import RiveComponent from '@rive-app/react-canvas';
import { Button } from '@bfe/moly';
import { useFm } from '@web3/base-hooks';
import styles from './style.module.scss';
import { CloseIcon } from '@bfe/moly-icons';
import { appPath } from '@web3/base-utils';
import { DrawLotteryStatus, LotteryType } from '@/services/lottery';
import { Page } from '@/config/track-config';
import { GroupRewardList } from './GroupRewardList';
import { RewardGrid } from './RewardDisplay/RewardGrid';
import { SingleRewardDisplay } from './RewardDisplay/SingleRewardDisplay';
import clsx from 'clsx';
import { getGroupRewards } from './GroupRewardList/getGroupRewards';

type IDrawResult = {
  drawResult: DrawLotteryStatus;
  onTryAgain: () => void;
  onClose: () => void;
  lotteryType: LotteryType;
};

export const WinResult = ({
  drawResult,
  onTryAgain,
  onClose,
  lotteryType,
}: IDrawResult) => {
  const fm = useFm();

  const groupRewards = getGroupRewards(drawResult.rewards);

  const isAbleToTryAgain =
    lotteryType === LotteryType.Single
      ? drawResult.remainingLotteryTimes >= 1
      : drawResult.remainingLotteryTimes >= 10;

  const rewardsText = groupRewards
    .map((reward) => `${reward.totalAmount} ${reward.rewardToken}`)
    .join(', ');

  const handleShare = () => {
    const twitterText = fm('claimedBybitWeb3Points', {
      Tokenlist: rewardsText,
      url: `${window.location.origin}/web3/points`,
    });

    window.top?.open(
      `https://x.com/intent/post?text=${encodeURIComponent(twitterText)}`
    );
  };

  const isSingleLottery = lotteryType === LotteryType.Single;

  return (
    <div className={'flex flex-col items-center'}>
      <p className="relative text-white text-lg font-normal mb-8">
        {fm('congrats')}

        <CloseIcon
          onClick={onClose}
          className={
            'absolute top-1 left-[calc(100%+90px)] cursor-pointer w-6 h-6 text-gray-400'
          }
        />
      </p>

      {isSingleLottery ? (
        <SingleRewardDisplay rewards={drawResult.rewards} />
      ) : (
        <div className="mb-6">
          <RewardGrid rewards={drawResult.rewards} />
        </div>
      )}

      {/* smaller height when the rewards length is less than 5 */}
      <RiveComponent
        src={`${appPath}/riv/confetti.riv`}
        className={clsx(styles['draw-content'], {
          'w-[300px] h-[300px]':
            isSingleLottery || drawResult.rewards.length >= 5,
          'w-[300px] h-[260px]':
            !isSingleLottery && drawResult.rewards.length < 5,
        })}
      />

      <div className="w-full mb-5">
        <GroupRewardList groupRewards={groupRewards} />
      </div>

      {isAbleToTryAgain ? (
        <div className="w-full mx-auto flex items-center justify-between gap-4 w-80">
          <Button
            variant="primary"
            onClick={onTryAgain}
            data-spm-even="Web3UserClick"
            data-spm-data={JSON.stringify({
              page_name: Page.RewardsPage,
              button_name: 'Web3Points_TryAgain',
            })}
            className="w-full"
          >
            {fm('letMeTryAgain')}
          </Button>
          <Button
            variant="secondary"
            onClick={handleShare}
            data-spm-even="Web3UserClick"
            data-spm-data={JSON.stringify({
              page_name: Page.RewardsPage,
              button_name: 'Web3Points_Share',
            })}
            className="w-full"
          >
            {fm('share1')}
          </Button>
        </div>
      ) : (
        <Button
          variant="primary"
          onClick={handleShare}
          data-spm-even="Web3UserClick"
          data-spm-data={JSON.stringify({
            page_name: Page.RewardsPage,
            button_name: 'Web3Points_Share',
          })}
          className="w-full"
        >
          {fm('share1')}
        </Button>
      )}
    </div>
  );
};
